import {Component, computed, HostListener, inject, OnInit, signal, ViewEncapsulation} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {BoService} from '../services/bo.service';

interface RawKey {
  _id: string;
  name: string;
  ban: boolean;
  index: string | number;
  enableList: string[];
}

interface RawList {
  _id: string;
  name: string;
  actif: boolean;
  dateEndValide: string;
  color?: string;
  description?: string;
  nfcMode?: string;
}

interface KeyItem {
  _id: string;
  name: string;
  initials: string;
  ban: boolean;
  index: number;
  enableList: string[];
  enabled: boolean;
  originalEnabled: boolean;
}

@Component({
  selector: 'app-configurer-general',
  templateUrl: './configurer-general.component.html',
  styleUrls: ['./configurer-general.component.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [FormsModule, RouterLink]
})
export class ConfigurerGeneralComponent implements OnInit {
  private boService = inject(BoService);

  loading = signal(true);
  saving = signal(false);
  savingPublish = signal(false);
  showPopup = signal(false);

  listName = signal('');
  listDate = signal('');
  colorTag = signal('0');
  description = signal('');
  nfcMode = signal('optional');
  actif = signal(false);

  original = signal<{ name: string; date: string; color: string; description: string; nfcMode: string }>({
    name: '', date: '', color: '0', description: '', nfcMode: 'optional'
  });

  keys = signal<KeyItem[]>([]);

  private listId = '';

  changeCount = computed(() => {
    let count = 0;
    const orig = this.original();
    if (this.listName() !== orig.name) count++;
    if (this.listDate() !== orig.date) count++;
    if (this.colorTag() !== orig.color) count++;
    if (this.description() !== orig.description) count++;
    if (this.nfcMode() !== orig.nfcMode) count++;
    for (const key of this.keys()) {
      if (key.enabled !== key.originalEnabled) count++;
    }
    return count;
  });

  hasChanges = computed(() => this.changeCount() > 0);

  nameError = signal('');

  ngOnInit() {
    const htitsec = document.getElementById('htitsec');
    if (htitsec) htitsec.innerHTML = 'Configurer';
    const htitsec2 = document.getElementById('htitsec2');
    if (htitsec2) htitsec2.innerHTML = 'Général';

    const menubs = document.querySelector('.menubs') as HTMLElement;
    if (menubs) menubs.removeAttribute('id');
    const menubs3 = document.querySelector('.menubs-3') as HTMLElement;
    if (menubs3) menubs3.id = 'actif1';
    const secmenu2bs = document.querySelector('.secmenu2bs') as HTMLElement;
    if (secmenu2bs) secmenu2bs.removeAttribute('id');
    const s3s1 = document.getElementsByClassName('3s1')[0] as HTMLElement | undefined;
    if (s3s1) s3s1.id = 'actif2';

    const wiw = window.innerWidth;
    const fles = document.getElementById('fles');
    if (wiw > 610) {
      if (htitsec) htitsec.style.display = 'inline';
      if (htitsec2) htitsec2.style.display = 'inline';
      if (fles) fles.style.display = 'inline';
    } else {
      if (htitsec2) htitsec2.style.display = 'inline';
      if (fles) fles.style.display = 'none';
    }

    const headerbo = document.querySelector('.headerbo') as HTMLElement;
    if (headerbo) {
      headerbo.style.borderColor = 'var(--ui-color-border)';
      headerbo.style.boxShadow = 'none';
    }

    window.scrollTo(0, 0);

    const infoUser = JSON.parse(localStorage.getItem('infoUser') || '{}') as { darkMode?: string; contrastMode?: string };
    const darkMode = infoUser.darkMode === 'on' ? 'on' : 'off';
    const contrastMode = infoUser.contrastMode === 'on' ? 'on' : 'off';

    const dataList = JSON.parse(localStorage.getItem('dataList') || '{}') as RawList;
    this.listId = dataList._id;

    (this.boService.getAllKeys() as Promise<RawKey[]>).then((data) => { // any-justifié: service non typé retourne Promise<unknown>
      let dataKeys: RawKey[] = (data || []).sort((a, b) => parseInt(String(a.index)) - parseInt(String(b.index)));
      for (const k of dataKeys) {
        if (!k.enableList) k.enableList = [];
      }

      this.loadInfoList(dataList);
      this.loadKeys(dataKeys);
      this.boService.screenMode(darkMode, contrastMode);

      const blocLoader = document.querySelector('.blocLoader') as HTMLElement;
      if (blocLoader) {
        setTimeout(() => { blocLoader.style.display = 'none'; }, 300);
      }
      this.loading.set(false);
    }).catch((error: unknown) => {
      this.boService.errorLoad(error);
    });
  }

  @HostListener('window:scroll')
  onScroll() {
    const headerbo = document.querySelector('.headerbo') as HTMLElement;
    if (!headerbo) return;
    if (window.scrollY > 110) {
      headerbo.style.borderColor = 'transparent';
      headerbo.style.boxShadow = 'var(--ui-shadow-sm)';
    } else {
      headerbo.style.borderColor = 'var(--ui-color-border)';
      headerbo.style.boxShadow = 'none';
    }
  }

  private loadInfoList(x: RawList) {
    this.actif.set(x.actif === true);

    const endDateParts = (x.dateEndValide || '').split(' ');
    const timeParts = (endDateParts[1] || '00:00:00').split(':');
    const hour = timeParts[0] + ':' + timeParts[1];
    const dateParts = (endDateParts[0] || '').split('-');
    const isoDate = dateParts[2] + '-' + dateParts[1] + '-' + dateParts[0] + 'T' + hour;

    const name = x.name || '';
    const color = x.color || '0';
    const desc = x.description || '';
    const nfc = x.nfcMode || 'optional';

    this.listName.set(name);
    this.listDate.set(isoDate);
    this.colorTag.set(color);
    this.description.set(desc);
    this.nfcMode.set(nfc);

    this.original.set({ name, date: isoDate, color, description: desc, nfcMode: nfc });
  }

  private loadKeys(dataKeys: RawKey[]) {
    const listId = this.listId;
    const items: KeyItem[] = dataKeys.map(k => {
      const enabled = (k.enableList as string[]).includes(listId);
      return {
        _id: k._id,
        name: k.name,
        initials: this.getInitials(k.name),
        ban: k.ban === true,
        index: parseInt(String(k.index)),
        enableList: [...k.enableList],
        enabled,
        originalEnabled: enabled
      };
    });
    this.keys.set(items);
  }

  private getInitials(name: string): string {
    const parts = name.trim().split(' ');
    return parts.length > 1
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : parts[0][0].toUpperCase();
  }

  toggleKey(keyId: string) {
    this.keys.update(keys => keys.map(k => {
      if (k._id !== keyId) return k;
      const newEnabled = !k.enabled;
      const newEnableList = newEnabled
        ? [...k.enableList, this.listId]
        : k.enableList.filter(id => id !== this.listId);
      return { ...k, enabled: newEnabled, enableList: newEnableList };
    }));
  }

  async save() {
    if (this.saving() || !this.hasChanges()) return;
    this.saving.set(true);

    const orig = this.original();
    const newData: Record<string, string | boolean> = {};

    if (this.listName() !== orig.name) {
      const trimmed = this.listName().trim();
      if (!trimmed) {
        this.nameError.set('Ce champ est requis');
        this.saving.set(false);
        return;
      }
      this.nameError.set('');
      this.listName.set(trimmed);
      newData['name'] = trimmed;
    }
    if (this.listDate() !== orig.date) {
      const val = this.listDate();
      if (val) {
        const [datePart, timePart] = val.split('T');
        const [y, m, d] = datePart.split('-');
        newData['dateEndValide'] = `${d}-${m}-${y} ${timePart}:00`;
      }
    }
    if (this.colorTag() !== orig.color) newData['color'] = this.colorTag();
    if (this.description() !== orig.description) newData['description'] = this.description();
    if (this.nfcMode() !== orig.nfcMode) newData['nfcMode'] = this.nfcMode();

    const aStaff = this.keys()
      .filter(k => k.enabled !== k.originalEnabled)
      .map(k => ({ id: k._id, index: k.index, enableList: k.enableList }));

    try {
      await this.boService.editList(newData);
      if (aStaff.length > 0) {
        await this.boService.editAllKeys({ aStaff });
      }
      this.original.set({
        name: this.listName(),
        date: this.listDate(),
        color: this.colorTag(),
        description: this.description(),
        nfcMode: this.nfcMode()
      });
      this.keys.update(keys => keys.map(k => ({ ...k, originalEnabled: k.enabled })));
    } finally {
      this.saving.set(false);
    }
  }

  cancel() {
    const orig = this.original();
    this.listName.set(orig.name);
    this.listDate.set(orig.date);
    this.colorTag.set(orig.color);
    this.description.set(orig.description);
    this.nfcMode.set(orig.nfcMode);
    this.keys.update(keys => keys.map(k => {
      const enableList = k.originalEnabled
        ? (k.enableList.includes(this.listId) ? k.enableList : [...k.enableList, this.listId])
        : k.enableList.filter(id => id !== this.listId);
      return { ...k, enabled: k.originalEnabled, enableList };
    }));
  }

  togglePublish() {
    if (this.savingPublish()) return;
    this.savingPublish.set(true);
    const newActif = !this.actif();
    this.actif.set(newActif);
    this.boService.editList({ actif: newActif }).then(() => {
      this.savingPublish.set(false);
    }).catch(() => {
      this.actif.set(!newActif);
      this.savingPublish.set(false);
    });
  }

  openPopup() {
    this.showPopup.set(true);
  }

  closePopup() {
    this.showPopup.set(false);
  }
}
